/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Icon } from '@components/atoms'
import { Button, CircularProgress, IconButton, TextField } from '@mui/material'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
import dayjs from 'dayjs'

import { ApiGetDataStatus } from '@redux/index'
import { InquiryType, InquiryCommentType, InquiryStatusLogType } from '@schemas'
import { INQUIRY_STATUS_LABEL, INQUIRY_DRAWER_WIDTH } from '@configs'
import { sanitizeReplyHtml, formatFileSize } from './replyHtml'

type TimelineItemType =
	| { type: 'comment'; data: InquiryCommentType; createdAt: string }
	| { type: 'status'; data: InquiryStatusLogType; createdAt: string }

interface PropsType {
	open: boolean
	inquiry: InquiryType | null
	detailStatus: ApiGetDataStatus
	comments: InquiryCommentType[]
	statusLogs: InquiryStatusLogType[]
	userList: { id: string; name: string }[]
	onClose: () => void
	onAddComment: (inquiryId: string, body: string) => void
	onDeleteComment: (inquiryId: string, commentId: string) => void
	/** 이메일 답변 다이얼로그 열기 — 카드의 버튼과 같은 것 */
	onReply: (inquiryId: string) => void
	/** 답변 첨부 받기 */
	onDownloadFile: (inquiryId: string, fileId: string) => void
}

/**
 * 대화 한 줄의 종류별 모양 (judahsWeb/INQUIRY_THREAD_PLAN.md §7).
 * 고객에게 보이는 것은 email · customer 뿐 — memo 에는 「내부」 를 붙여 그 사실을 분명히 한다.
 */
const COMMENT_KIND_VIEW: Record<
	InquiryCommentType['kind'],
	{ icon: string; chip: string; chipClass: string }
> = {
	email: { icon: 'email-outline', chip: '이메일 답변', chipClass: 'outline' },
	customer: { icon: 'account-outline', chip: '고객', chipClass: 'solid' },
	memo: { icon: 'comment-text-outline', chip: '내부', chipClass: 'faint' },
}

/** 이메일 답변 본문 — HTML 이 있으면 정화해서 그리고 접어 둔다. 옛 답변(텍스트뿐)은 텍스트로 */
const EmailReplyBody = ({ comment }: { comment: InquiryCommentType }) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const safeHtml = useMemo(
		() => (comment.bodyHtml ? sanitizeReplyHtml(comment.bodyHtml) : ''),
		[comment.bodyHtml],
	)

	return (
		<View className="timelineBody emailBody">
			{comment.subject !== '' && <div className="emailSubject">{comment.subject}</div>}
			{safeHtml !== '' ? (
				<>
					<div
						className={`emailHtml ${isExpanded ? 'expanded' : ''}`}
						dangerouslySetInnerHTML={{ __html: safeHtml }}
					/>
					<button
						type="button"
						className="boLink emailToggle"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						{isExpanded ? '접기' : '펼치기'}
					</button>
				</>
			) : (
				<div className="emailText">{comment.body}</div>
			)}
		</View>
	)
}

/**
 * 활동 타임라인 드로어 — 목록 오른쪽에 붙는다.
 * 다른 카드의 [타임라인] 을 누르면 내용만 바뀌고, X 를 눌러야 닫힌다.
 * 항목은 색 대신 아이콘 + 칩 + 헤어라인으로 갈린다 — 이메일 답변 · 고객 댓글 · 내부 메모 · 상태.
 * 이메일 답변과 고객 댓글은 고객 My Messages 에도 같은 순서로 보인다(INQUIRY_THREAD_PLAN §7).
 */
export const InquiryTimelineDrawer = (props: PropsType) => {
	const {
		open,
		inquiry,
		detailStatus,
		comments,
		statusLogs,
		userList,
		onClose,
		onAddComment,
		onDeleteComment,
		onReply,
		onDownloadFile,
	} = props

	const [commentBody, setCommentBody] = useState('')
	const scrollRef = useRef<HTMLDivElement | null>(null)

	// 문의가 바뀌면 쓰던 코멘트는 비운다
	useEffect(() => {
		setCommentBody('')
	}, [inquiry?.id])

	const userNameMap = useMemo(() => {
		const map: Record<string, string> = {}
		userList.forEach(u => {
			map[u.id] = u.name
		})
		return map
	}, [userList])

	const timeline: TimelineItemType[] = useMemo(() => {
		const items: TimelineItemType[] = [
			...comments.map(c => ({
				type: 'comment' as const,
				data: c,
				createdAt: c.createdAt,
			})),
			...statusLogs.map(s => ({
				type: 'status' as const,
				data: s,
				createdAt: s.createdAt,
			})),
		]
		items.sort(
			(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		)
		return items
	}, [comments, statusLogs])

	// 새 활동이 붙으면 맨 아래로
	useEffect(() => {
		const el = scrollRef.current
		if (el) el.scrollTop = el.scrollHeight
	}, [timeline.length, inquiry?.id])

	const submit = useCallback(() => {
		if (!inquiry || !commentBody.trim()) return
		onAddComment(inquiry.id, commentBody)
		setCommentBody('')
	}, [inquiry, commentBody, onAddComment])

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				submit()
			}
		},
		[submit],
	)

	return (
		<View
			css={style}
			className={`timelineDrawer ${open ? 'open' : ''}`}
			aria-hidden={!open}
		>
			<View className="drawerInner">
				<View className="drawerHead">
					<View className="drawerTitle">
						<span className="boKicker">Timeline</span>
						{inquiry && (
							<Text category="c1" className="drawerSubtitle">
								{`${inquiry.name || '-'} · ${inquiry.email || '-'}`}
							</Text>
						)}
					</View>
					<IconButton size="small" onClick={onClose} title="닫기">
						<Icon name="close" style={{ fontSize: 20, color: 'inherit' }} />
					</IconButton>
				</View>

				<View className="timelineView" ref={scrollRef}>
					{!inquiry ? (
						<View className="emptyTimeline">
							<Text category="p2" className="emptyText">
								카드의 [타임라인] 을 누르면 여기에 나옵니다.
							</Text>
						</View>
					) : detailStatus === 'loading' && timeline.length === 0 ? (
						<View className="emptyTimeline">
							<CircularProgress size={24} />
						</View>
					) : timeline.length === 0 ? (
						<View className="emptyTimeline">
							<Text category="p2" className="emptyText">
								아직 활동 기록이 없습니다.
							</Text>
						</View>
					) : (
						timeline.map(item => {
							if (item.type === 'comment') {
								const comment = item.data
								const authorName =
									comment.authorName || userNameMap[comment.authorId] || comment.authorId
								const kindView = COMMENT_KIND_VIEW[comment.kind] ?? COMMENT_KIND_VIEW.memo
								return (
									<View
										key={`c-${comment.id}`}
										className={`timelineItem kind-${comment.kind}`}
									>
										<View className="timelineHeader">
											<Icon
												name={kindView.icon}
												style={{ fontSize: 16, color: mono.ink }}
											/>
											<span className={`boChip ${kindView.chipClass} kindChip`}>
												{kindView.chip}
											</span>
											<Text category="s2" className="timelineAuthor">
												{authorName}
											</Text>
											<Text category="c1" className="timelineDate">
												{dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')}
											</Text>
											<IconButton
												size="small"
												onClick={() => onDeleteComment(inquiry.id, comment.id)}
												title="코멘트 삭제"
												sx={{ marginLeft: 'auto', padding: '2px' }}
											>
												<Icon name="close" style={{ fontSize: 14, color: 'inherit' }} />
											</IconButton>
										</View>
										{comment.kind === 'email' ? (
											<EmailReplyBody comment={comment} />
										) : (
											<Text category="p2" className="timelineBody">
												{comment.body}
											</Text>
										)}
										{comment.files.length > 0 && (
											<View className="timelineFiles">
												{comment.files.map(aFile => (
													<button
														key={aFile.id}
														type="button"
														className="fileChip"
														onClick={() => onDownloadFile(inquiry.id, aFile.id)}
														title="받기"
													>
														<Icon
															name="paperclip"
															style={{ fontSize: 13, color: 'inherit' }}
														/>
														{`${aFile.fileName} · ${formatFileSize(aFile.size)}`}
													</button>
												))}
											</View>
										)}
									</View>
								)
							}
							const log = item.data
							const changerName =
								log.changedByName || userNameMap[log.changedBy] || log.changedBy
							return (
								<View key={`s-${log.id}`} className="timelineItem statusItem">
									<View className="timelineHeader">
										<Icon
											name="swap-horizontal"
											style={{ fontSize: 16, color: mono.faint }}
										/>
										<Text category="c1" className="timelineDate">
											{dayjs(log.createdAt).format('YYYY-MM-DD HH:mm')}
										</Text>
										<Text category="s2" className="timelineAuthor">
											{changerName}
										</Text>
									</View>
									<Text category="p2" className="timelineBody statusBody">
										{`상태 변경: ${
											INQUIRY_STATUS_LABEL[log.fromStatus] ?? log.fromStatus ?? '-'
										} → ${INQUIRY_STATUS_LABEL[log.toStatus] ?? log.toStatus}${
											log.note ? ` (${log.note})` : ''
										}`}
									</Text>
								</View>
							)
						})
					)}
				</View>

				<View className="commentInputView">
					<Text category="c1" className="memoHint">
						여기 쓰는 코멘트는 내부 메모입니다. 고객에게는 이메일 답변으로 보내세요.
					</Text>
					<TextField
						value={commentBody}
						onChange={e => setCommentBody(e.target.value)}
						onKeyDown={onKeyDown}
						placeholder={inquiry ? '코멘트를 입력하세요... (⌘/Ctrl+Enter 등록)' : ''}
						multiline
						rows={3}
						fullWidth
						size="small"
						disabled={!inquiry}
					/>
					<View className="commentActions">
						<Button
							variant="text"
							onClick={() => inquiry && onReply(inquiry.id)}
							disabled={!inquiry || !inquiry.email}
						>
							이메일 답변
						</Button>
						<Button
							variant="outlined"
							onClick={submit}
							disabled={!inquiry || !commentBody.trim()}
						>
							등록
						</Button>
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({
	...boShared,
	flexShrink: 0,
	width: 0,
	height: '100%',
	overflow: 'hidden',
	transition: 'width 200ms ease-out',
	background: mono.surface,
	borderLeft: `1px solid ${mono.line}`,

	'&.open': {
		width: INQUIRY_DRAWER_WIDTH,
	},

	'.drawerInner': {
		width: INQUIRY_DRAWER_WIDTH,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: '0 12px 0 20px',
	},

	'.drawerHead': {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 8,
		padding: '14px 0 12px',
		borderBottom: `1px solid ${mono.line}`,
		flexShrink: 0,
		textAlign: 'left',
	},
	'.drawerTitle': {
		flex: 1,
		minWidth: 0,
		'.drawerSubtitle': {
			marginTop: 6,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			color: mono.dim,
		},
	},

	'.timelineView': {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
		padding: '4px 0',
		overscrollBehavior: 'contain',
	},
	'.emptyTimeline': {
		display: 'flex',
		justifyContent: 'center',
		padding: '2rem 1rem',
		textAlign: 'center',
		'.emptyText': { color: mono.faint, fontWeight: 300 },
	},
	'.timelineItem': {
		padding: '12px 0',
		borderBottom: `1px solid ${mono.line}`,
		textAlign: 'left',
	},
	'.statusItem': {
		'.timelineAuthor': { color: mono.dim },
	},
	'.timelineHeader': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginBottom: 4,
	},
	'.timelineDate': { whiteSpace: 'nowrap', color: mono.faint, fontWeight: 300 },
	'.kindChip': { height: 18, padding: '0 6px', fontSize: 10 },
	// 고객 댓글은 왼쪽 선으로 스태프 쪽과 갈린다
	'.kind-customer': {
		borderLeft: `2px solid ${mono.ink}`,
		paddingLeft: 10,
		marginLeft: -12,
	},
	'.emailBody': {
		whiteSpace: 'normal',
		'.emailSubject': { fontSize: 14, fontWeight: 500, marginBottom: 6 },
		'.emailText': { fontSize: 14, whiteSpace: 'pre-wrap' },
		// 드로어의 다른 항목(p2)과 같은 크기 — 메일 HTML 이 body 글자 크기를 물려받지 않게
		'.emailHtml': {
			fontSize: 14,
			fontWeight: 300,
			lineHeight: 1.5,
			maxHeight: '7.5em',
			overflow: 'hidden',
			'&.expanded': { maxHeight: 'none' },
			p: { margin: '0 0 8px' },
			img: { maxWidth: '100%', height: 'auto' },
			a: { color: mono.ink },
		},
		'.emailToggle': { marginTop: 6, fontSize: 12 },
	},
	'.timelineFiles': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		paddingLeft: 24,
		marginTop: 8,
	},
	'.fileChip': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		height: 24,
		padding: '0 8px',
		fontSize: 12,
		color: mono.ink,
		background: 'transparent',
		border: `1px solid ${mono.line}`,
		borderRadius: 2,
		cursor: 'pointer',
		'&:hover': { borderColor: mono.ink },
	},
	'.memoHint': { color: mono.faint, fontWeight: 300, marginBottom: 6, textAlign: 'left' },
	'.timelineAuthor': { fontWeight: 500, color: mono.ink },
	'.timelineBody': {
		paddingLeft: 24,
		marginTop: 4,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		fontWeight: 300,
		color: mono.ink,
		lineHeight: '1.5 !important',
		'&.statusBody': { color: mono.dim },
	},

	'.commentInputView': {
		display: 'flex',
		flexDirection: 'column',
		padding: '12px 0 12px',
		borderTop: `1px solid ${mono.line}`,
		flexShrink: 0,
	},
	'.commentActions': {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 8,
		marginTop: 8,
	},

	// 좁은 화면에서는 목록 위에 덮는다
	'@media (max-width: 900px)': {
		position: 'fixed',
		top: 0,
		right: 0,
		bottom: 0,
		height: '100vh',
		zIndex: 1200,
		'&.open': { width: `min(${INQUIRY_DRAWER_WIDTH}, 100vw)` },
		'.drawerInner': { width: `min(${INQUIRY_DRAWER_WIDTH}, 100vw)`, padding: '0 16px' },
	},
})
